import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useServiceProduct } from "@/hooks/site/useProducts";
import CTASection from "@/pages/site-home/components/CTASection";

const ICON_BY_CATEGORY: Record<string, string> = {
  Residential: "ri-home-smile-line",
  Commercial: "ri-building-2-line",
  "Long Distance": "ri-roadster-line",
  Packing: "ri-archive-2-line",
  Storage: "ri-inbox-archive-line",
  Specialty: "ri-music-2-line",
};

const COLOR_BY_CATEGORY: Record<string, string> = {
  Residential: "from-brand-500 to-brand-700",
  Commercial: "from-ink-800 to-ink-900",
  "Long Distance": "from-brand-600 to-brand-800",
  Packing: "from-amber-500 to-orange-600",
  Storage: "from-emerald-600 to-emerald-800",
  Specialty: "from-rose-600 to-rose-800",
};

export default function SiteServiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { product, loading } = useServiceProduct(id);

  const finalPrice = useMemo(() => {
    if (!product) return 0;
    return product.discount_enabled && product.discount_price != null
      ? product.discount_price
      : product.price;
  }, [product]);

  if (loading) {
    return (
      <SiteLayout navVariant="solid">
        <div className="min-h-[60vh] pt-32 flex items-center justify-center text-ink-500 text-sm">
          <i className="ri-loader-4-line animate-spin text-2xl text-brand-500 mr-2"></i>
          Loading service…
        </div>
      </SiteLayout>
    );
  }

  if (!product) {
    return (
      <SiteLayout navVariant="solid">
        <div className="min-h-[60vh] pt-32 flex flex-col items-center justify-center gap-4 text-center px-4">
          <i className="ri-emotion-sad-line text-5xl text-ink-300"></i>
          <h1 className="font-display text-2xl font-bold text-ink-900">Service not found</h1>
          <p className="text-ink-500 text-sm">This service package doesn't exist or has been removed.</p>
          <Link
            to="/site/services"
            className="px-5 py-2.5 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer"
          >
            Back to all services
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const categoryName = product.product_categories?.name ?? "Residential";
  const icon = ICON_BY_CATEGORY[categoryName] ?? "ri-truck-line";
  const color = COLOR_BY_CATEGORY[categoryName] ?? "from-brand-500 to-brand-700";
  const image = product.media?.[0]?.url ?? "";
  const inStock = (product.stock ?? 0) > 0;

  return (
    <SiteLayout navVariant="solid">
      {/* Breadcrumb spacer */}
      <div className="pt-24 md:pt-28 bg-gradient-to-br from-[#FAF5EC] via-cream-50 to-[#F5EBDA]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs font-semibold text-ink-500 pt-4">
            <Link to="/" className="hover:text-brand-600 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/site/services" className="hover:text-brand-600 cursor-pointer">Services</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-ink-900 truncate">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#FAF5EC] via-cream-50 to-[#F5EBDA] pt-8 md:pt-10 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Image */}
          <div className="lg:col-span-7">
            <div className="relative rounded-2xl overflow-hidden h-[380px] md:h-[540px] bg-cream-100">
              {image && (
                <img src={image} alt={product.name} className="w-full h-full object-cover object-top" />
              )}
              <div className={`absolute top-5 left-5 w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${color} text-white`}>
                <i className={`${icon} text-3xl`}></i>
              </div>
              <div className="absolute top-5 right-5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[11px] font-bold text-ink-900 uppercase tracking-wider">
                {categoryName}
              </div>
            </div>
          </div>

          {/* Buy card */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-xs uppercase tracking-[0.18em] text-brand-600 font-bold mb-3">
              {categoryName} Service
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight tracking-tight mb-5">
              {product.name}
            </h1>

            <div className="flex items-baseline gap-3 mb-2">
              <div className="font-display text-4xl font-bold text-ink-900">
                ${finalPrice.toLocaleString()}
              </div>
              {product.discount_enabled && product.discount_price != null && (
                <div className="text-base text-ink-400 line-through">${product.price.toLocaleString()}</div>
              )}
              <div className="text-sm font-semibold text-ink-500">/ project</div>
            </div>
            <div className="text-xs text-ink-500 mb-6">Flat-rate · all-in pricing · no hidden fees</div>

            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-2.5 text-sm text-ink-700">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <i className="ri-shield-check-line"></i>
                </span>
                Full-replacement-value insurance included
              </div>
              <div className="flex items-center gap-2.5 text-sm text-ink-700">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <i className="ri-time-line"></i>
                </span>
                On-time guarantee — late fee waived if we miss
              </div>
              <div className="flex items-center gap-2.5 text-sm text-ink-700">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <i className="ri-team-line"></i>
                </span>
                Background-checked W-2 crew
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                disabled={!inStock}
                onClick={() => navigate(`/site/checkout/${product.id}`)}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md text-sm font-bold bg-brand-500 hover:bg-brand-600 disabled:bg-ink-300 disabled:cursor-not-allowed text-white cursor-pointer whitespace-nowrap transition-colors"
              >
                {inStock ? (
                  <>
                    Book This Service
                    <i className="ri-arrow-right-line"></i>
                  </>
                ) : (
                  "Currently unavailable"
                )}
              </button>
              <Link
                to="/site/contact"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md text-sm font-semibold border border-ink-200 hover:border-ink-300 hover:bg-white text-ink-800 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-chat-quote-line"></i>
                Get a Custom Quote First
              </Link>
            </div>

            <div className="mt-6 pt-6 border-t border-ink-200/60 flex items-center gap-2 text-xs text-ink-500">
              <i className="ri-customer-service-2-line"></i>
              Questions? Call us at
              <a href="tel:+13475550142" className="font-bold text-ink-800 hover:text-brand-600 cursor-pointer">
                (347) 555-0142
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-xs uppercase tracking-[0.18em] text-brand-600 font-bold mb-3">
            What's included
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 mb-8">
            Everything you get with this package
          </h2>
          <article
            className="prose prose-ink max-w-none text-ink-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_li]:text-sm [&_li]:text-ink-700 [&_p]:text-base [&_p]:mb-4"
            dangerouslySetInnerHTML={{ __html: product.description ?? "" }}
          />
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
}